import { ErrorMessage, Field, Form, Formik, FormikProps } from 'formik';
import { useState } from 'react';
import Select from 'react-select';
import { maxLength, minLength, required, requiredMultiSelect, requiredSelect, schemaValidate, isNumber, noValidation } from '../../../../../helpers/validation-helpers';
import FormErrorMessage from '../../../../common/form-elements/form-error-message/form-error-message';
import Input from '../../../../common/form-elements/input/input';
import { AUTO_REFRESH_SELECT_LABEL } from '../../device-inventory/constants';
import { AUTO_REFRESH_OPTIONS } from '../../widget-constants';
import { IConfigurationFormData, IConfigurationFormProps, handleFormFieldChangeValue, IValueObject } from '../models/configuration-form.props';
import { DATASETS_OPTIONS, FORM_DATASETS_PLACEHOLDER, FORM_TITLE_PLACEHOLDER } from '../constants';
import Message from './message/Message';

const ActivityCountConfigurationForm = ({
    initialValues,
    onFormSubmit,
    onCancel
}: IConfigurationFormProps) => {
    const [validateAutoRotate, setValidateAutoRotate] = useState(initialValues.datasets.length < 2 ? false : true);

    const changeDatasets = (props: FormikProps<IConfigurationFormData>) => (value: IValueObject[]) => {
        props.setFieldValue('datasets', value);
        if (value.length < 2) {
            props.setFieldValue('rotateDatasetsInterval', undefined);
            setValidateAutoRotate(false);
            return;
        }
        if (!props.values.rotateDatasetsInterval) {
            props.setFieldValue('rotateDatasetsInterval', 5);
        }
        setValidateAutoRotate(true);
    };

    const handleFormFieldChange = (field: string, props: FormikProps<IConfigurationFormData>) => (value: handleFormFieldChangeValue) => {
        props.setFieldValue(field, value);
    };

    const handleFormSubmit = (values: IConfigurationFormData) => {
        onFormSubmit(values);
    };

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={handleFormSubmit}
        >
            {(props) => {
                return (
                    <Form>
                        <div className="glu-modal__body">
                            <div className="glu-content__section">
                                <div className="glu-form-row">
                                    <div className="glu-label">Widget title</div>
                                    <Field
                                        as={Input}
                                        name="title"
                                        className="glu-form-input-pad"
                                        autoFocus
                                        placeholder={FORM_TITLE_PLACEHOLDER}
                                        value={props.values.title}
                                        onChange={handleFormFieldChange('title', props)}
                                        validate={schemaValidate(
                                            [required(), minLength(), maxLength(30)]
                                        )}
                                        invalid={
                                            props.errors.title
                                            && props.touched.title
                                        }
                                    />
                                    <ErrorMessage name="title" component={FormErrorMessage} />

                                </div>
                                <div className="glu-form-row">
                                    <div className="glu-label">Datasets</div>
                                    <Field
                                        as={Select}
                                        name="datasets"
                                        placeholder={FORM_DATASETS_PLACEHOLDER}
                                        aria-label={FORM_DATASETS_PLACEHOLDER}
                                        value={props.values.datasets}
                                        options={DATASETS_OPTIONS}
                                        onChange={changeDatasets(props)}
                                        className="glu-select glu-form-input-pad"
                                        classNamePrefix="react-select"
                                        isMulti
                                        validate={schemaValidate(requiredMultiSelect())}
                                        invalid={
                                            props.errors.datasets
                                            && props.touched.datasets
                                        }
                                    />
                                    <ErrorMessage name="datasets" component={FormErrorMessage} />
                                </div>
                                <div className="glu-form-row">
                                    <div className="glu-label">Auto rotate datasets (in seconds)</div>
                                    <Field
                                        as={Input}
                                        name="rotateDatasetsInterval"
                                        className="glu-form-input-pad"
                                        placeholder="Auto rotate"
                                        value={props.values.rotateDatasetsInterval}
                                        onChange={handleFormFieldChange('rotateDatasetsInterval', props)}
                                        validate={validateAutoRotate ? schemaValidate([isNumber(0)]) : noValidation}
                                        isDisabled={props.values.datasets.length < 2}
                                        invalid={
                                            props.errors.rotateDatasetsInterval
                                            && props.touched.rotateDatasetsInterval
                                        }
                                    />
                                    <ErrorMessage name="rotateDatasetsInterval" component={FormErrorMessage} />

                                </div>
                                <div className="glu-form-row">
                                    <div className="glu-label">Auto refresh</div>
                                    <Field
                                        data-testid="auto-refresh"
                                        as={Select}
                                        name="autoRefresh"
                                        aria-label={AUTO_REFRESH_SELECT_LABEL}
                                        value={props.values?.autoRefresh}
                                        options={AUTO_REFRESH_OPTIONS}
                                        onChange={handleFormFieldChange(
                                            'autoRefresh',
                                            props
                                        )}
                                        className="glu-select glu-form-input-pad"
                                        classNamePrefix="react-select"
                                        placeholder="Select Auto Refresh rate"
                                        validate={schemaValidate(
                                            [requiredSelect().nullable()]
                                        )}
                                        invalid={
                                            props.errors.autoRefresh
                                            && props.touched.autoRefresh
                                        }
                                    />
                                    <ErrorMessage name="autoRefresh" component={FormErrorMessage} />
                                </div>
                            </div>
                        </div>
                        <div className="glu-message-container">
                            <Message visible={!!props.values.autoRefresh} message="Your data retention policy may limit the number of changes we can tally." type="warning" />
                        </div>
                        <footer className="glu-modal__footer">
                            <div className="glu-flex-grow glu-flex-row">
                                <div className="glu-flex-grow text-right">
                                    <button
                                        onClick={onCancel}
                                        className="glu-btn glu-btn--secondary"
                                        type="button"
                                    >
                                        Cancel
                                    </button>
                                </div>
                                <div className="glu-flex-shrink">
                                    <button
                                        className="glu-btn glu-btn--primary"
                                        type="submit"
                                    >
                                        Save
                                    </button>
                                </div>
                            </div>
                        </footer>
                    </Form>
                );
            }}
        </Formik>
    );
};

export default ActivityCountConfigurationForm;
